/**
 * Notification Preferences Service
 * 
 * Service layer for managing company and user notification preferences.
 * Handles CRUD operations for notification_preferences table.
 */

import { supabase } from '@/integrations/supabase/client';
import type { NotificationTypeKey } from '@/constants/notificationTypes';

// ============================================================================
// TYPES
// ============================================================================

export interface NotificationPreference {
  id: string;
  company_id: string;
  profile_id: string | null;
  role_scope: string | null;
  type_key: string;
  enabled: boolean;
  force_enabled: boolean | null;
  channels: string[];
  threshold_override: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyNotificationPreference {
  type_key: string;
  enabled: boolean;
  channels: string[];
  force_enabled: boolean;
}

export interface UpdatePreferenceInput {
  type_key: string;
  enabled?: boolean;
  channels?: string[];
  force_enabled?: boolean;
}

// ============================================================================
// COMPANY PREFERENCES (Admin)
// ============================================================================

/**
 * Get all company-level notification preferences
 */
export async function getCompanyNotificationPreferences(companyId: string): Promise<CompanyNotificationPreference[]> {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('type_key, enabled, channels, force_enabled')
    .eq('company_id', companyId)
    .is('profile_id', null)
    .is('role_scope', null);

  if (error) {
    console.error('[NotificationPreferences] Error fetching company preferences:', error);
    throw error;
  }

  return (data || []).map(pref => {
    // Parse channels from JSONB - can be array of strings or other JSON types
    let channels: string[] = ['in_app'];
    if (Array.isArray(pref.channels)) {
      channels = pref.channels.filter((c): c is string => typeof c === 'string');
    }
    
    return {
      type_key: pref.type_key,
      enabled: pref.enabled ?? true,
      channels,
      force_enabled: pref.force_enabled ?? false,
    };
  });
}

/**
 * Upsert a company-level notification preference
 */
export async function upsertCompanyNotificationPreference(
  companyId: string,
  preference: UpdatePreferenceInput
): Promise<void> {
  const { error } = await supabase
    .from('notification_preferences')
    .upsert({
      company_id: companyId,
      profile_id: null,
      role_scope: null,
      type_key: preference.type_key,
      enabled: preference.enabled ?? true,
      channels: preference.channels ?? ['in_app'],
      force_enabled: preference.force_enabled ?? false,
      updated_at: new Date().toISOString(),
    }, {
      // Uses unique constraint: uq_notification_preferences_compound
      // UNIQUE NULLS NOT DISTINCT (company_id, profile_id, role_scope, type_key)
      onConflict: 'company_id,profile_id,role_scope,type_key',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('[NotificationPreferences] Error upserting company preference:', error);
    throw error;
  }
}

/**
 * Batch update company notification preferences
 */
export async function batchUpdateCompanyPreferences(
  companyId: string,
  preferences: UpdatePreferenceInput[]
): Promise<void> {
  const records = preferences.map(pref => ({
    company_id: companyId,
    profile_id: null,
    role_scope: null,
    type_key: pref.type_key,
    enabled: pref.enabled ?? true,
    channels: pref.channels ?? ['in_app'],
    force_enabled: pref.force_enabled ?? false,
    updated_at: new Date().toISOString(),
  }));

  const { error } = await supabase
    .from('notification_preferences')
    .upsert(records, {
      // Uses unique constraint: uq_notification_preferences_compound
      // UNIQUE NULLS NOT DISTINCT (company_id, profile_id, role_scope, type_key)
      onConflict: 'company_id,profile_id,role_scope,type_key',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('[NotificationPreferences] Error batch updating preferences:', error);
    throw error;
  }
}

// ============================================================================
// USER PREFERENCES (Personal)
// ============================================================================

export interface UserNotificationPreference {
  type_key: string;
  enabled: boolean;
  channels: string[];
}

/**
 * Get user's notification preferences
 */
export async function getUserNotificationPreferences(profileId: string, companyId: string): Promise<UserNotificationPreference[]> {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('type_key, enabled, channels')
    .eq('company_id', companyId)
    .eq('profile_id', profileId)
    .is('role_scope', null);

  if (error) {
    console.error('[NotificationPreferences] Error fetching user preferences:', error);
    throw error;
  }

  return (data || []).map(pref => {
    let channels: string[] = ['in_app'];
    if (Array.isArray(pref.channels)) {
      channels = pref.channels.filter((c): c is string => typeof c === 'string');
    }
    
    return {
      type_key: pref.type_key,
      enabled: pref.enabled ?? true,
      channels,
    };
  });
}

/**
 * Update user's notification preference
 */
export async function upsertUserNotificationPreference(
  profileId: string,
  companyId: string,
  preference: { type_key: string; enabled?: boolean; channels?: string[] }
): Promise<void> {
  const { error } = await supabase
    .from('notification_preferences')
    .upsert({
      company_id: companyId,
      profile_id: profileId,
      role_scope: null,
      type_key: preference.type_key,
      enabled: preference.enabled ?? true,
      channels: preference.channels ?? ['in_app'],
      force_enabled: false,
      updated_at: new Date().toISOString(),
    }, {
      // Uses unique constraint: uq_notification_preferences_compound
      // UNIQUE NULLS NOT DISTINCT (company_id, profile_id, role_scope, type_key)
      onConflict: 'company_id,profile_id,role_scope,type_key',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('[NotificationPreferences] Error upserting user preference:', error);
    throw error;
  }
}

// ============================================================================
// MUTES
// ============================================================================

export interface NotificationMute {
  id: string;
  type_key: string;
  muted_at: string;
  expires_at: string | null;
}

/**
 * Get user's active (non-expired) muted notification types.
 * 
 * Filters out expired mutes to match backend behavior in check_notification_muted(),
 * which uses: (m.expires_at IS NULL OR m.expires_at > now())
 * 
 * Expired mute rows remain in DB for up to 7 days for audit purposes
 * but should not affect UI display.
 */
export async function getUserMutes(profileId: string, companyId: string): Promise<NotificationMute[]> {
  const { data, error } = await supabase
    .from('notification_mutes')
    .select('id, type_key, muted_at, expires_at')
    .eq('profile_id', profileId)
    .eq('company_id', companyId)
    // Only return active mutes: no expiry OR expiry in the future
    // Matches backend logic in check_notification_muted() RPC
    .or('expires_at.is.null,expires_at.gt.now()');

  if (error) {
    console.error('[NotificationPreferences] Error fetching mutes:', error);
    throw error;
  }

  return data || [];
}

/**
 * Add a mute for a notification type
 */
export async function addMute(
  profileId: string,
  companyId: string,
  typeKey: string,
  expiresAt?: string
): Promise<void> {
  const { error } = await supabase
    .from('notification_mutes')
    .upsert({
      profile_id: profileId,
      company_id: companyId,
      type_key: typeKey,
      muted_at: new Date().toISOString(),
      expires_at: expiresAt ?? null,
    }, {
      onConflict: 'profile_id,type_key',
      ignoreDuplicates: false,
    });

  if (error) {
    console.error('[NotificationPreferences] Error adding mute:', error);
    throw error;
  }
}

/**
 * Remove a mute
 */
export async function removeMute(profileId: string, typeKey: string): Promise<void> {
  const { error } = await supabase
    .from('notification_mutes')
    .delete()
    .eq('profile_id', profileId)
    .eq('type_key', typeKey);

  if (error) {
    console.error('[NotificationPreferences] Error removing mute:', error);
    throw error;
  }
}

// ============================================================================
// RESOLVED PREFERENCES (Role + Wildcard Support)
// ============================================================================

export interface ResolvedNotificationPreference {
  type_key: string;
  enabled: boolean;
  channels: string[];
  force_enabled: boolean;
  source_level: 'force' | 'user' | 'role' | 'company' | 'default';
}

/**
 * Get fully resolved notification preferences for the current authenticated user.
 * This handles role-level inheritance, wildcard matching, and force-enabled.
 * Use this for UI display to show effective preferences.
 * 
 * SECURITY: The RPC always uses auth.uid() - no cross-user access is possible.
 */
export async function getResolvedUserPreferences(): Promise<ResolvedNotificationPreference[]> {
  const { data, error } = await supabase.rpc('get_resolved_notification_preferences');

  if (error) {
    console.error('[NotificationPreferences] Error fetching resolved preferences:', error);
    throw error;
  }

  return (data || []).map((pref: {
    type_key: string;
    enabled: boolean;
    channels: unknown;
    force_enabled: boolean;
    source_level: string;
  }) => {
    // Parse channels from JSONB
    let channels: string[] = ['in_app'];
    if (Array.isArray(pref.channels)) {
      channels = pref.channels.filter((c): c is string => typeof c === 'string');
    }
    
    return {
      type_key: pref.type_key,
      enabled: pref.enabled ?? true,
      channels,
      force_enabled: pref.force_enabled ?? false,
      source_level: (pref.source_level as ResolvedNotificationPreference['source_level']) ?? 'default',
    };
  });
}

// ============================================================================
// NOTIFICATIONS (Read/Unread)
// ============================================================================

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_unread_notification_count');

  if (error) {
    console.error('[NotificationPreferences] Error getting unread count:', error);
    return 0;
  }

  return data ?? 0;
}

/**
 * Mark a notification as read
 */
export async function markNotificationRead(notificationId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('mark_notification_read', {
    p_notification_id: notificationId
  });

  if (error) {
    console.error('[NotificationPreferences] Error marking as read:', error);
    return false;
  }

  return data ?? false;
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(): Promise<number> {
  const { data, error } = await supabase.rpc('mark_all_notifications_read');

  if (error) {
    console.error('[NotificationPreferences] Error marking all as read:', error);
    return 0;
  }

  return data ?? 0;
}

/**
 * Dismiss a notification
 */
export async function dismissNotification(notificationId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('dismiss_notification', {
    p_notification_id: notificationId
  });

  if (error) {
    console.error('[NotificationPreferences] Error dismissing notification:', error);
    return false;
  }

  return data ?? false;
}
